import { stringify } from 'query-string';
import { resolve, ImResponse, doorClient, messDoorClient } from '.';

export interface RegionGroupListParams {
  channel: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
  enable?: 'true' | 'false';
  groupType?: Phrase.Group['groupType'];
}

export function regionGroupList(params: RegionGroupListParams) {
  return resolve(
    doorClient.get<ImResponse<Phrase.Region | undefined>>(
      '/overseaim-hi-manager/v1/queryPhraseGroup',
      { params },
    ),
  );
}

// 获取短语组
export type GroupListParamsV2 = Omit<RegionGroupListParams, 'region'> & {
  filterPerm?: boolean;
};
export type GroupListDataV2 = Omit<Phrase.Region, 'associationEnable'> | undefined;
export function getPhraseGroupV2(params: GroupListParamsV2) {
  return resolve(
    doorClient.get<ImResponse<GroupListDataV2>>('/overseaim-hi-manager/v1/queryPhraseGroupV2', {
      params,
    }),
  );
}

export interface CreateGroupBody extends Pick<Phrase.Group, 'channel' | 'name' | 'groupType'> {
  region: General.UpperCaseRegion;
}

export function createGroup(body: CreateGroupBody) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/createPhraseGroup', body),
  );
}

export interface UpdateGroupBody extends Pick<Phrase.Group, 'name' | 'applyScope'> {
  phraseGroupId: string;
}

export function updateGroup(body: UpdateGroupBody) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/updatePhraseGroup', body),
  );
}

export interface UpdateGroupEnableBody {
  phraseGroupId: string;
  enable: boolean;
}

export function updateGroupEnable(body: UpdateGroupEnableBody) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/updatePhraseGroupEnable', body),
  );
}

export interface DeleteGroupBody {
  phraseGroupId: string;
}

export function deleteGroup(body: DeleteGroupBody) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/deletePhraseGroup', body),
  );
}

export interface CreateBody
  extends Pick<Phrase.Phrase, 'content' | 'translationContent' | 'images'> {
  phraseGroupId: string;
}

export function create(body: CreateBody) {
  return resolve(doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/createPhrase', body));
}

export interface UpdateBody extends Omit<CreateBody, 'phraseGroupId'> {
  phraseId: string;
}

export function update(body: UpdateBody) {
  return resolve(doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/updatePhrase', body));
}

export interface DeleteBody {
  phraseId: string;
}

export function deletePhrase(body: DeleteBody) {
  return resolve(doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/deletePhrase', body));
}

export interface UpdateAssociationEnableBody {
  channel: General.PlatformUpperCase;
  region: General.UpperCaseRegion[];
  associationEnable: boolean;
}

export function updateAssociationEnable(body: UpdateAssociationEnableBody) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/updateAssociationEnable', body),
  );
}

export function statistic() {
  return resolve(
    doorClient.get<ImResponse<Phrase.Statistic | undefined>>(
      '/overseaim-hi-manager/v1/queryChannelRegionInfo',
    ),
  );
}

export interface PhraseGroupV2Body {
  id: string;
  groupType: Phrase.Group['groupType'];
  name: string;
  channel: General.PlatformUpperCaseTunnel;
  applyScope: Phrase.Group['applyScope'];
}

// 创建短语组 V2
export function createPhraseGroupV2(payload: PhraseGroupV2Body) {
  return resolve(
    doorClient.post<ImResponse<{ id: string } | undefined>>(
      '/overseaim-hi-manager/v1/createPhraseGroupV2',
      payload,
    ),
  );
}

// 短语组排序
export interface SortPhraseGroupV2Body {
  sortMap: Record<string, string>;
}
export function sortPhraseGroupV2(payload: SortPhraseGroupV2Body) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/sortPhraseGroup', payload),
  );
}

export interface PhrasesTextElement {
  text: {
    content: string;
    translationContent: string;
  };
}
export interface PhrasesStickerElement {
  sticker: {
    pid: string;
    sid: string;
  };
}
export interface PhrasesResourceElement {
  resource: Phrase.PhraseResourceRelation;
}
export interface PhraseAttachmentElement {
  attachment: {
    text: string;
    attachments: Conversation.MsgAttachmentInfo[];
  };
}
type PhraseElement =
  | PhrasesTextElement
  | PhrasesStickerElement
  | PhrasesResourceElement
  | PhraseAttachmentElement;
export interface PhraseRootV2Phrase {
  region: General.UpperCaseRegion;
  phraseElems: {
    elems: PhraseElement[];
  };
}
export interface PhraseRootV2Body {
  phraseRootId: string;
  phraseGroupId: string;
  shortcutKey?: string;
  tags?: string[];
  phrases: PhraseRootV2Phrase[];
}

// 创建快捷短语 v2
export function createPhraseV2(payload: Omit<PhraseRootV2Body, 'phraseRootId'>) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/overseaim-hi-manager/v1/createPhraseRoot', payload),
  );
}

// 更新快捷短语 v2
export function updatePhraseV2(payload: PhraseRootV2Body) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/overseaim-hi-manager/v1/updatePhraseRoot', payload),
  );
}

export interface QueryPhrasesParamsV2 {
  groupType?: Phrase.Group['groupType'];
  // 不传则查询所有
  phraseGroupId?: string;
  channel?: General.PlatformUpperCaseTunnel;
  eTag?: string;
  filterPerm?: boolean;
}

export interface PhrasesData {
  content?: string;
  translationContent?: string;
  id: string;
  phraseElems: {
    elems: PhraseElement[];
  };
  phraseGroupId: string;
  phraseRootId: string;
  phraseType: number;
  region: General.UpperCaseRegion;
  images: any[];
  resources: any[];
}

export interface PhrasesRootData {
  id: string;
  phraseGroupId: string;
  groupName: string;
  shortcutKey: string;
  sortNumber: string;
  channel: General.PlatformUpperCaseTunnel;
  groupType: Phrase.Group['groupType'];
  tags: string[];
  phraseMap: Partial<Record<General.UpperCaseRegion, PhrasesData>>;
}

export interface PhrasesRootV2 {
  eTag: string;
  phraseRoots: PhrasesRootData[];
  phraseGroups: Phrase.Group[];
  /**
   * 关闭联想的站点
   */
  disabledRegion: Record<
    General.CaselessPlatformTunnel,
    {
      regions: General.UpperCaseRegion[];
    }
  >;
}

// 查询快捷短语列表 v2
export function queryPhrasesV2(params: QueryPhrasesParamsV2) {
  return doorClient.get<ImResponse<PhrasesRootV2>>('/overseaim-hi-manager/v1/queryPhraseRoot', {
    params,
  });
}

export interface DeletePhraseV2Body {
  phraseRootId: string;
}

export function deletePhraseV2(body: DeletePhraseV2Body) {
  return resolve(
    doorClient.post<ImResponse<PhrasesRootV2>>('/overseaim-hi-manager/v1/deletePhraseRoot', body),
  );
}

// 排序
export function sortPhraseV2(payload: SortPhraseGroupV2Body) {
  return resolve(
    doorClient.post<ImResponse<void>>('/overseaim-hi-manager/v1/sortPhraseRoot', payload),
  );
}

export interface ValidatePhraseShortcutBody {
  shortcutKey: string;
  channel: General.PlatformUpperCaseTunnel;
  groupType?: Phrase.Group['groupType'];
  phraseRootId?: string;
}
// 校验快捷键是否重复
export function validatePhraseRootShortcut(payload: ValidatePhraseShortcutBody) {
  return resolve(
    doorClient.post<ImResponse<{ isConflict: boolean }>>(
      '/overseaim-hi-manager/v1/validatePhraseRootShortcut',
      payload,
    ),
  );
}

export function batchDeletePhrase(rootId: string[]) {
  return doorClient.delete('/overseaim-hi-manager/v1/batchDeletePhrase', {
    params: { rootId },
    paramsSerializer: (params) => stringify(params, { arrayFormat: 'none' }),
  });
}

export function getPublicAccountList() {
  return resolve(
    doorClient.get<
      ImResponse<
        {
          name: string;
          userId: string;
          enable: boolean;
        }[]
      >
    >('/overseaim-hi-manager/v1/getShareList'),
  );
}

export function updatePublicAccount(body: { accountId: string; enable: boolean }) {
  return doorClient.post('/overseaim-hi-manager/v1/updateShare', body);
}

export function getPublicPhraseList(params?: {
  assistantId?: string[];
  platform?: General.PlatformUpperCase[];
  phraseGroupId?: string[];
}) {
  return resolve(
    doorClient.get<
      ImResponse<{
        phraseRoots: PhrasesRootData[];
        phraseGroups: (PhraseGroupV2Body & { userId: string })[];
      }>
    >('/overseaim-hi-manager/v1/getSharePhraseList', {
      params,
      paramsSerializer: (params) => stringify(params, { arrayFormat: 'none' }),
    }),
  );
}

export function importPublicPhrases(body: { phrase: PhrasesRootData[] }) {
  return doorClient.post('/overseaim-hi-manager/v1/importSharePhrase', body);
}

export function copyPhraseToPlatforms(body: {
  platform: General.PlatformUpperCase[];
  phrase: PhrasesRootData[];
}) {
  return doorClient.post('/overseaim-hi-manager/v1/importFromCopy', body);
}

export function uploadPhraseExcel(file: File, lang: General.SystemLanguage) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('lang', lang);
  return resolve(
    messDoorClient.post<
      ImResponse<{
        url: string;
        successCount: number;
        failCount: number;
      }>
    >('/overseaim-hi-manager/v1/verifyExcel', formData),
  );
}

export function importExcelPhrases(body: {
  url: string;
  platform: General.PlatformUpperCase[];
  type: 'GROUP' | 'PERSON';
  autoTranslate: boolean;
}) {
  return doorClient.post('/overseaim-hi-manager/v1/importFromExcel', body);
}
